// src/features/system/orchestrator/api/remoteAuthApi.ts
//
// FE API layer for the RemoteAuthGrant entity — persistent remote-env
// pairing with configurable TTL, auto-renewal, and revocation.
//
// Entity hierarchy:
//   RemotePairing (remote_env + remote_url)
//     └─ RemoteAuthGrant (access_token + refresh_token + TTL)
//
import { bff } from '@/lib/http/bff';

// ── TTL presets (seconds) ──────────────────────────────────────────────────

export const GRANT_TTL_PRESETS: Record<string, number> = {
  '5m':    300,
  '1h':    3_600,
  '24h':   86_400,
  '7d':    604_800,
  '30d':   2_592_000,
  'never': 0,
};

export const GRANT_TTL_LABELS: Record<string, string> = {
  '5m':    '5 min',
  '1h':    '1 hour',
  '24h':   '24 hours',
  '7d':    '7 days',
  '30d':   '30 days',
  'never': 'Never expire',
};

export const DEFAULT_GRANT_TTL = 604_800; // 7 days

// ── Types ──────────────────────────────────────────────────────────────────

export type GrantStatus = 'active' | 'expired' | 'revoked';
export type GrantType = 'device_bearer' | 'service_jwt' | 'session_token';

export interface RemoteAuthGrantMeta {
  id: string;
  remote_url: string;
  remote_env: string;
  tenant_id: number | null;
  grant_type: GrantType;
  ttl_seconds: number;
  issued_at: string;
  expires_at: string | null;
  auto_renew: boolean;
  status: GrantStatus;
  last_used_at: string | null;
  last_refreshed_at: string | null;
  created_by: string;
  token_expires_at_ms: number;
  has_refresh: boolean;
}

export interface GrantStatusResponse {
  has_grant: boolean;
  grant_id?: string;
  status?: GrantStatus;
  remote_env: string;
  grant_type?: GrantType;
  ttl_seconds?: number;
  issued_at?: string;
  expires_at?: string | null;
  auto_renew?: boolean;
  last_used_at?: string | null;
  last_refreshed_at?: string | null;
  token_valid?: boolean;
}

export interface GrantListResponse {
  grants: RemoteAuthGrantMeta[];
  ttl_presets: Record<string, number>;
}

export interface UpsertGrantParams {
  remote_url: string;
  remote_env: string;
  tenant_id?: number | null;
  grant_type?: GrantType;
  access_token: string;
  refresh_token?: string | null;
  refresh_endpoint?: string | null;
  ttl_seconds?: number;
  auto_renew?: boolean;
  token_expires_in?: number;
}

// ── API Calls ──────────────────────────────────────────────────────────────

/** List all grants (metadata only, no raw tokens). */
export const fetchGrants = (): Promise<GrantListResponse> =>
  bff.get<GrantListResponse>('/orchestrator/migrate/scopes/auth/grants');

/** Create or update a grant for a (remote_env, tenant_id) pair. */
export const upsertGrant = (params: UpsertGrantParams): Promise<RemoteAuthGrantMeta> =>
  bff.post<RemoteAuthGrantMeta>('/orchestrator/migrate/scopes/auth/grants', params as any);

/** Update TTL or auto_renew for an existing grant. */
export const updateGrant = (
  id: string,
  patch: { ttl_seconds?: number; auto_renew?: boolean },
): Promise<RemoteAuthGrantMeta> =>
  bff.patch<RemoteAuthGrantMeta>(`/orchestrator/migrate/scopes/auth/grants/${id}`, patch as any);

/** Revoke a grant — explicit disconnect. */
export const revokeGrant = (id: string): Promise<{ status: string; id: string }> =>
  bff.del<{ status: string; id: string }>(`/orchestrator/migrate/scopes/auth/grants/${id}`);

/** Check active grant status for a remote_env. */
export const fetchGrantStatus = (
  remoteEnv: string,
  tenantId?: number | null,
): Promise<GrantStatusResponse> =>
  bff.get<GrantStatusResponse>('/orchestrator/migrate/scopes/auth/grants/status', {
    remote_env: remoteEnv,
    ...(tenantId != null ? { tenant_id: String(tenantId) } : {}),
  } as any);
